import {Link as RouterLink} from 'react-router-dom';

import moment from 'moment';

import {Routing} from 'nutshell-core/routing';

import {colors} from 'shells/colors/colors';
import {GlossaryButton} from 'shells/glossary-button/glossary-button';
import {ExternalLinkIcon} from 'shells/icon';
import type {CantSendStatusEnum, ChannelStatus} from 'shells/inbox/types';
import {PageTitleBar} from 'shells/layout/page-title-bar';
import type {Props as PageTitleBarProps} from 'shells/layout/page-title-bar';
import {Stack} from 'shells/layout/stack/stack';
import {Link} from 'shells/link/link';
import type {LinkConfig} from 'shells/sidebar-nav/link-item';
import {StatusDot} from 'shells/status-selector/status-dot';
import {Body} from 'shells/typography/body';

import {
    ThreadStatus,
    ThreadType,
    ContactFilter,
    ChatbotResponseTimingEnum,
    ThreadBotStatus,
    InboxChannelType,
} from 'nutshell-graphql-types';
import type {
    GetChatSettings_chatSettings as ChatSettings,
    GetLatestThreads_latestThreads_edges_node as Thread,
    InboxStatusFragment,
    InboxThreadFragment,
    GetInboxSidebarCounts_inboxThreadCounts_sms as SmsThreadCounts,
    GetInboxSidebarCounts_inboxThreadCounts_chat as ChatThreadCounts,
    User,
} from 'nutshell-graphql-types';

import {appHistory} from '../../history';
import {addNotification} from '../../notifications/index';

import {ChatHeaderButtons} from './chat/chat-header-buttons';
import {ChatSettingsHeaderButtons} from './chat/settings/buttons';
import {EmailHeaderButtons} from './email/email-header-buttons';
import {FacebookHeaderButtons} from './facebook/facebook-header-buttons';
import {SmsHeaderButtons} from './sms';
import {WhatsAppHeaderButtons} from './whatsapp/whatsapp-header-buttons';

export const getThreadStatusFromSelectedTab = (tab?: string | null): ThreadStatus => {
    switch (tab) {
        case 'open':
            return ThreadStatus.OPEN;
        case 'closed':
            return ThreadStatus.CLOSED;
        case 'waiting':
            return ThreadStatus.AWAITING_REPLY;
        case 'scheduled':
            return ThreadStatus.SCHEDULED;
        case 'snoozed':
            return ThreadStatus.SNOOZED;
        case 'archived':
            return ThreadStatus.ARCHIVED;
        case 'failed':
            return ThreadStatus.FAILED;
        case 'unassigned':
            return ThreadStatus.UNASSIGNED;
        case 'inactive':
            return ThreadStatus.CLOSED;
        case 'active':
        case 'unidentified':
        default:
            return ThreadStatus.OPEN;
    }
};

export const getContactFilterForSelectedTab = (tab: string): ContactFilter => {
    switch (tab) {
        case 'active':
            return ContactFilter.HAS_CONTACT;
        case 'unidentified':
            return ContactFilter.NO_CONTACT;
        case 'inactive':
        default:
            return ContactFilter.ALL;
    }
};

export const getThreadStatusFromEffectiveStatus = (
    effectiveStatus?: ThreadStatus | null
): ThreadStatus | null | undefined => {
    switch (effectiveStatus) {
        case ThreadStatus.AWAITING_REPLY:
        case ThreadStatus.SCHEDULED:
        case ThreadStatus.SNOOZED:
        case ThreadStatus.FAILED:
        case ThreadStatus.UNASSIGNED:
            return ThreadStatus.OPEN;
        default:
            return effectiveStatus;
    }
};

export function isShellyThread(status: InboxThreadFragment): boolean {
    return status.externalChannel.type === InboxChannelType.TYPE_STUB_SHELLY;
}

export const getCanThreadBeReassigned = (thread: InboxThreadFragment): boolean => {
    const status = getThreadStatusFromEffectiveStatus(thread.effectiveStatus);

    // Can’t reassign our stubbed shelly thread, since it’s not a real thread
    // and one exists per-user based on the assignee.
    return status === 'OPEN' && !isShellyThread(thread);
};

export function getEmptyStateValues(
    status: ThreadStatus,
    canAccessSetup: boolean,
    threadType?: ThreadType | null,
    selectedTab?: string | null,
    chatSettings?: ChatSettings | null
) {
    // For chatbot thread tabs, check if chat or chatbot is not enabled
    if (selectedTab === 'active' || selectedTab === 'unidentified' || selectedTab === 'inactive') {
        if (chatSettings != null) {
            const isChatEnabled = chatSettings.isChatEnabled ?? false;
            const chatbotResponseTiming = chatSettings.chatbot?.responseTiming;
            const isChatbotEnabled = chatbotResponseTiming !== ChatbotResponseTimingEnum.NEVER;

            // Show NOT_CONFIGURED if chat is not enabled or chatbot is not enabled
            if (!isChatEnabled || !isChatbotEnabled) {
                return EMPTY_STATE_VALUES.NOT_CONFIGURED;
            }

            // Check for chatbot-specific tabs first
            switch (selectedTab) {
                case 'active':
                    return EMPTY_STATE_VALUES.ACTIVE;
                case 'unidentified':
                    return EMPTY_STATE_VALUES.UNIDENTIFIED;
                case 'inactive':
                    return EMPTY_STATE_VALUES.INACTIVE;
            }
        }
    }

    if (status === ThreadStatus.SCHEDULED) {
        return canAccessSetup
            ? EMPTY_STATE_VALUES.SCHEDULED
            : EMPTY_STATE_VALUES.SCHEDULED_NO_SETUP;
    }

    if (threadType === ThreadType.EMAIL) {
        switch (status) {
            case ThreadStatus.OPEN:
                return EMPTY_STATE_VALUES.EMAIL_OPEN;
            case ThreadStatus.CLOSED:
                return EMPTY_STATE_VALUES.EMAIL_CLOSED;
        }
    }

    // Special case for thread types where conversations can be initiated from Nutshell
    if (status === ThreadStatus.OPEN && (!threadType || threadType === ThreadType.SMS)) {
        return EMPTY_STATE_VALUES.OPEN_NUTSHELL_INITIATED;
    }

    return EMPTY_STATE_VALUES[status];
}

const EMPTY_STATE_VALUES = {
    OPEN_NUTSHELL_INITIATED: {
        header: 'There are currently no open threads',
        text: 'Here you’ll find all ongoing conversations, whether initiated by you or others. Since there are currently no active conversations, hang tight, and threads will appear as they come in, or as you start new ones!',
        image: 'chatting-bubble',
        list: 'No open threads.',
    },
    OPEN: {
        header: 'There are currently no open threads',
        text: 'Here you’ll find all ongoing conversations. Since there are currently no active conversations, hang tight, and threads will appear as they come in!',
        image: 'chatting-bubble',
        list: 'No open threads.',
    },
    CLOSED: {
        header: 'There are currently no closed threads',
        text: 'This is where resolved conversations are stored, whether closed by you or the recipient opting out. At the moment, no threads have been closed.',
        image: 'bank-safe',
        list: 'No closed threads.',
    },
    AWAITING_REPLY: {
        header: 'There are currently no threads waiting for reply',
        text: 'This is where conversations awaiting your reply will reside. At the moment, no one’s waiting for your attention.',
        image: 'sending-mail',
        list: 'No threads waiting for your reply.',
    },
    SNOOZED: {
        header: 'There are currently no snoozed threads',
        text: 'Threads snoozed by you or your team will appear here until they’re manually reopened or their snooze period ends.',
        image: 'sleeping-man',
        list: 'Nothing currently snoozed.',
    },
    SCHEDULED: {
        header: 'There are currently no scheduled threads',
        text: (
            <span>
                Automated text messages scheduled to send appear here. Use{' '}
                <Link size='normal' variant='primary' newTab={true} href='/setup/sales-automation'>
                    Nutshell’s sales automation
                </Link>{' '}
                to automatically text new leads, or when a lead enters a certain stage.
            </span>
        ),
        image: 'stop-clock',
        list: 'Nothing scheduled.',
    },
    SCHEDULED_NO_SETUP: {
        header: 'There are currently no scheduled threads',
        text: (
            <span>
                Automated text messages scheduled to send appear here. Use Nutshell’s sales
                automation to automatically text new leads, or when a lead enters a certain stage.
            </span>
        ),
        image: 'stop-clock',
        list: 'Nothing scheduled.',
    },
    ARCHIVED: {
        header: 'There are currently no archived threads',
        text: 'This is where your archived threads will appear.',
        image: 'no-messages',
        list: 'Nothing here.',
    },
    FAILED: {
        header: 'There are currently no threads with failed messages',
        text: 'Anytime a message fails to send for any reason, it will appear here.',
        image: 'no-messages',
        list: 'Smooth sailing. No failed messages.',
    },
    UNASSIGNED: {
        header: 'There are currently no unassigned threads',
        text: 'Threads that are not assigned to a specific team member will appear here.',
        image: 'needs-reply',
        list: 'No unassigned threads.',
    },
    ACTIVE: {
        header: 'There are currently no active threads',
        text: (
            <div>
                Here you’ll find all active chatbot conversations. Hang tight, and threads will
                appear as they come in!
                <div className='mt-12'>
                    <Link
                        to='/inbox/chatbot-threads/inactive'
                        // @ts-expect-error
                        as={RouterLink}
                        variant='primary'
                    >
                        View inactive threads
                    </Link>
                </div>
            </div>
        ),
        image: 'chatbot-thread-empty-state',
        list: 'No active threads',
    },
    UNIDENTIFIED: {
        header: 'There are currently no unidentified threads',
        text: (
            <div>
                Here you’ll find all unidentified chatbot conversations. Hang tight, and threads
                will appear as they come in!
                <div className='mt-12'>
                    <Link
                        to='/inbox/chatbot-threads/inactive'
                        // @ts-expect-error
                        as={RouterLink}
                        variant='primary'
                    >
                        View inactive threads
                    </Link>
                </div>
            </div>
        ),
        image: 'chatbot-thread-empty-state',
        list: 'No unidentified threads',
    },
    INACTIVE: {
        header: 'There are currently no inactive threads',
        text: 'Here you’ll find all inactive chatbot conversations. Hang tight, and threads will appear as they come in!',
        image: 'chatbot-thread-empty-state',
        list: 'No inactive threads',
    },
    NOT_CONFIGURED: {
        header: 'Set up your chatbot to see threads appear here!',
        text: (
            <div>
                <Link
                    to='/inbox/settings/chat/chatbot-behavior'
                    // @ts-expect-error
                    as={RouterLink}
                    variant='primary'
                    icon={ExternalLinkIcon}
                >
                    Go to chatbot settings
                </Link>
            </div>
        ),
        image: 'chatbot-thread-empty-state',
    },
    EMAIL_OPEN: {
        header: 'There are currently no active email threads',
        text: (
            <Stack spacing={16}>
                <Body size='small'>
                    Set up your email inbox settings to see threads appear here.
                </Body>
                <Link
                    to='/inbox/settings/email'
                    // @ts-expect-error
                    as={RouterLink}
                    variant='primary'
                    icon={ExternalLinkIcon}
                    size='small'
                >
                    Email inbox settings
                </Link>
            </Stack>
        ),
        image: 'email-thread-empty-state',
        list: 'No active email threads.',
    },
    EMAIL_CLOSED: {
        header: 'There are currently no closed email threads',
        text: 'Closed email conversations will appear here once you start using the shared email inbox.',
        image: 'email-thread-empty-state',
        list: 'No closed email threads.',
    },
} as const;

export const getThreadListEmptyStateText = (tab?: string | null): string => {
    switch (tab) {
        case 'open':
            return EMPTY_STATE_VALUES.OPEN.list;
        case 'closed':
            return EMPTY_STATE_VALUES.CLOSED.list;
        case 'waiting':
            return EMPTY_STATE_VALUES.AWAITING_REPLY.list;
        case 'scheduled':
            return EMPTY_STATE_VALUES.SCHEDULED.list;
        case 'snoozed':
            return EMPTY_STATE_VALUES.SNOOZED.list;
        case 'archived':
            return EMPTY_STATE_VALUES.ARCHIVED.list;
        case 'failed':
            return EMPTY_STATE_VALUES.FAILED.list;
        case 'active':
            return EMPTY_STATE_VALUES.ACTIVE.list;
        case 'unidentified':
            return EMPTY_STATE_VALUES.UNIDENTIFIED.list;
        case 'inactive':
            return EMPTY_STATE_VALUES.INACTIVE.list;
        default:
            return 'Nothing found.';
    }
};

export const getChannelEventsForTimeline = (channelEvents: any[]): any[] => {
    // Add message timestamps to message sent more than 3 hours after the previous event
    const eventsWithMessageTimestamps = addMessageTimestamps(channelEvents);

    // Combine a reassignment event (including DELETE and ADD events) into one displayable timeline event
    const eventsWithReassignments = combineEvents(eventsWithMessageTimestamps);

    // Sort to show newest at bottom
    return eventsWithReassignments.reverse();
};

export const addMessageTimestamps = (channelEvents: any[]): any[] => {
    return channelEvents.reduce<Array<any>>(
        (newEventArray, currentEvent, currentIndex, seedArray) => {
            if (currentIndex === 0 || currentEvent.payload.__typename !== 'Message') {
                return newEventArray.concat(currentEvent);
            }

            const messageWithTimestamp = getTimestampForMessage(
                currentEvent,
                seedArray[currentIndex + 1]
            );

            return newEventArray.concat(messageWithTimestamp);
        },
        []
    );
};

const getTimestampForMessage = (currentEvent: any, previousEvent?: any | null): any => {
    if (
        previousEvent &&
        isMessageEventMoreThanThreeHoursAfterPreviousEvent(
            currentEvent.changeTime,
            previousEvent.changeTime
        )
    ) {
        return {
            ...currentEvent,
            payload: {...currentEvent.payload, timestampToShow: currentEvent.changeTime},
        };
    }

    return currentEvent;
};

function isMessageEventMoreThanThreeHoursAfterPreviousEvent(
    currentEventTime: any,
    previousEventTime: any
) {
    return moment.unix(currentEventTime).isAfter(moment.unix(previousEventTime).add(3, 'hours'));
}

export const combineEvents = (channelEvents: any[]): any[] => {
    let justCombined = false;

    return channelEvents.reduce<Array<any>>(
        (newEventArray, currentEvent, currentIndex, seedArray) => {
            if (currentEvent.payload.__typename === 'Assignment') {
                const combinedEvent = reassignmentEventsCombiner(
                    currentEvent,
                    seedArray[currentIndex + 1]
                );

                if (!justCombined) {
                    newEventArray.push(combinedEvent ? combinedEvent : currentEvent);
                }
                justCombined = Boolean(combinedEvent);

                return newEventArray;
            } else if (currentEvent.payload.__typename === 'Thread') {
                const updatedEvent = threadEventsUpdater(currentEvent, seedArray[currentIndex + 1]);

                return newEventArray.concat(updatedEvent);
            } else return newEventArray.concat(currentEvent);
        },
        []
    );
};

// Copied/simplified from timeline/helpers.js
const reassignmentEventsCombiner = (
    currentEvent: any,
    nextEvent?: any | null
): any | null | undefined => {
    if (
        nextEvent &&
        nextEvent.payload.__typename === 'Assignment' &&
        nextEvent.changeType === 'DELETED' &&
        nextEvent.changeTime === currentEvent.changeTime
    ) {
        const updatedEvent = currentEvent;
        updatedEvent.previousAssignee = nextEvent.payload.assigneeEntity;

        return updatedEvent;
    }
};

const threadEventsUpdater = (currentEvent: any, nextEvent?: any | null): any | null | undefined => {
    if (
        nextEvent &&
        nextEvent.payload.__typename === 'Thread' &&
        nextEvent.changes[0] &&
        nextEvent.changes[0].attribute === 'closed_time' &&
        nextEvent.changes[0].newValue !== null
    ) {
        const updatedEvent = currentEvent;
        updatedEvent.changeType = 'REOPENED';

        return updatedEvent;
    } else {
        return currentEvent;
    }
};

export const getCantSendStatusEnum = (input: {
    inboxStatus: InboxStatusFragment | null | undefined;
    userCanUseSms: boolean;
    isInboxPage: boolean;
    channelStatus: ChannelStatus | null | undefined;
    selectedThread: Thread | null | undefined;
    hasSmsCampaigns: boolean;
}): CantSendStatusEnum | null | undefined => {
    const {
        inboxStatus,
        userCanUseSms,
        isInboxPage,
        channelStatus,
        selectedThread,
        hasSmsCampaigns,
    } = input;

    const inboxStatusString = inboxStatus && inboxStatus.status;

    const enrollmentNotStarted = !inboxStatusString || inboxStatusString === 'NOT_STARTED';

    const enrollmentSuccessful =
        inboxStatusString === 'FORM_APPROVED' || inboxStatusString === 'VALID';

    const enrollmentInProgress = inboxStatusString === 'FORM_IN_PROGRESS';

    const enrollmentError = inboxStatusString === 'ERROR';

    const noNutshellChannel = inboxStatusString === 'NUMBER_SETUP';

    const invalidNutshellChannel = inboxStatus && inboxStatus.phoneStatus.status !== 'VALID';

    const isChannelBlocked = Boolean(channelStatus && channelStatus === 'OPTED_OUT');

    const isChannelUndeliverable = Boolean(channelStatus && channelStatus === 'UNDELIVERABLE');

    const isChannelMissingContactInfo = Boolean(
        channelStatus && channelStatus === 'MISSING_CONTACT_INFO'
    );
    const isChannelInvalid = Boolean(channelStatus && channelStatus !== 'VALID');

    const hasNoPlan = inboxStatus && inboxStatus.planStatus.status === 'NO_PLAN';
    const hasNoCredits = inboxStatus && inboxStatus.planStatus.status === 'NO_CREDITS';

    const isThreadClosed = selectedThread && !selectedThread.isOpen;
    const isThreadAssignedToCurrentUser = selectedThread && selectedThread.isAssignedToCurrentUser;

    const isStubEntity = selectedThread && selectedThread.stubEntity;

    if (isStubEntity) {
        if (isInboxPage && isThreadClosed) {
            return 'THREAD_CLOSED_INBOX_PAGE';
        }

        if (!isInboxPage && isThreadClosed) {
            return 'THREAD_CLOSED_SIDEBAR';
        }

        return null;
    }

    if (!userCanUseSms && !hasSmsCampaigns) {
        return 'NO_RBAC_PERMISSION';
    }

    if (enrollmentNotStarted && hasNoCredits) {
        return 'NOT_STARTED_NO_CREDITS';
    }

    if (enrollmentNotStarted && !hasNoCredits) {
        return 'NOT_STARTED';
    }

    if (enrollmentInProgress) {
        return 'IN_PROGRESS';
    }

    if (enrollmentError) {
        return 'ENROLLMENT_ERROR';
    }

    if (noNutshellChannel) {
        return 'NO_NUTSHELL_CHANNEL';
    }

    if (invalidNutshellChannel) {
        return 'INVALID_NUTSHELL_CHANNEL';
    }

    if (hasNoPlan && enrollmentSuccessful && !hasSmsCampaigns) {
        return 'NO_INBOX_PLAN';
    }

    if (hasNoCredits && enrollmentSuccessful) {
        return 'NO_REMAINING_CREDITS';
    }

    if (isChannelBlocked) {
        return 'CHANNEL_BLOCKED';
    }

    if (isChannelUndeliverable) {
        return 'CHANNEL_UNDELIVERABLE';
    }

    if (isInboxPage && isThreadClosed) {
        return 'THREAD_CLOSED_INBOX_PAGE';
    }

    if (!isInboxPage && isThreadClosed) {
        return 'THREAD_CLOSED_SIDEBAR';
    }

    if (!isInboxPage && isThreadAssignedToCurrentUser === false) {
        if (selectedThread?.botStatus === ThreadBotStatus.BOT_ENABLED) {
            return 'CHATBOT_MANAGED';
        }

        return 'NOT_ASSIGNED';
    }

    if (isChannelMissingContactInfo) {
        return 'CHANNEL_MISSING_CONTACT_INFO';
    }

    if (isChannelInvalid) {
        return 'CHANNEL_INVALID';
    }

    return null;
};

export function getPageTitleBar(
    tab: string | null | undefined,
    status: string | null | undefined,
    subSetting: string | null | undefined,
    showHeaderButtons: boolean,
    onOpenChatbotLearnMoreModal: () => void,
    onOpenChatbotThreadsLearnMoreModal: () => void,
    onOpenSharedEmailInboxLearnMoreModal?: () => void
) {
    if (shouldHidePageTitleBar(tab, status)) {
        return null;
    }

    let content: PageTitleBarProps | null | undefined = null;
    const selectedTab = tab;

    const chatbotLearnMoreButton = (
        <GlossaryButton onClick={onOpenChatbotLearnMoreModal} buttonText='Learn more' />
    );

    const chatbotThreadsLearnMoreButton = (
        <GlossaryButton onClick={onOpenChatbotThreadsLearnMoreModal} buttonText='Learn more' />
    );

    const sharedEmailInboxLearnMoreButton = onOpenSharedEmailInboxLearnMoreModal ? (
        <GlossaryButton onClick={onOpenSharedEmailInboxLearnMoreModal} buttonText='Learn more' />
    ) : null;

    switch (selectedTab) {
        case 'dashboard':
            content = {
                title: 'Dashboard',
                subheaderPrimaryText: 'Your chats and text messages, all-in-one.',
            };
            break;
        case 'sms':
            content = {
                title: 'SMS',
                subheaderPrimaryText: 'A place to connect with customers through texts',
                buttons: showHeaderButtons && <SmsHeaderButtons />,
            };
            break;
        case 'chat':
            content = {
                title: 'Chat',
                subheaderPrimaryText:
                    'Connect with web visitors and generate leads using our AI chatbot',
                buttons: <ChatHeaderButtons />,
                glossaryButton: chatbotLearnMoreButton,
            };
            break;
        case 'messenger':
            content = {
                title: 'Messenger',
                subheaderPrimaryText: 'A place to connect with customers through Messenger',
                buttons: NutClientConfig.hasFacebookMessenger && (
                    <FacebookHeaderButtons threadType={ThreadType.MESSENGER} />
                ),
            };
            break;
        case 'instagram':
            content = {
                title: 'Instagram',
                subheaderPrimaryText: 'A place to connect with customers through Instagram',
                buttons: NutClientConfig.hasInstagram && (
                    <FacebookHeaderButtons threadType={ThreadType.INSTAGRAM} />
                ),
            };
            break;
        case 'whatsapp':
            content = {
                title: 'WhatsApp',
                subheaderPrimaryText: 'A place to connect with customers through WhatsApp',
                buttons: NutClientConfig.hasWhatsApp && <WhatsAppHeaderButtons />,
            };
            break;
        case 'engagement':
            content = {
                title: 'Engagement',
                subheaderPrimaryText:
                    'Engagement Suite offers a convenient way to communicate, keeping your connections strong and responsive using SMS, Chat, Facebook Messenger, and Instagram.',
            };
            break;
        case 'all':
            content = {
                title: 'All channels',
                subheaderPrimaryText: 'View all of your channels threads here',
            };
            break;
        case 'email':
            content = {
                title: 'Email',
                subheaderPrimaryText:
                    'A centralized inbox for managing shared email conversations with your team.',
                buttons: <EmailHeaderButtons />,
                glossaryButton: sharedEmailInboxLearnMoreButton,
            };
            break;
        case 'chatbot-threads':
            content = {
                title: 'Chatbot threads',
                subheaderPrimaryText:
                    'Conversations that users have had with your website’s chatbot.',
                buttons: <ChatHeaderButtons shouldShowChatbotReportButton={true} />,
                glossaryButton: chatbotThreadsLearnMoreButton,
            };
            break;
        case 'settings':
            switch (status) {
                case 'chat':
                    content = {
                        title: 'Chat settings',
                        subheaderPrimaryText:
                            'Configure chat and additional engagement options for your website',
                        buttons: <ChatSettingsHeaderButtons />,
                        glossaryButton: chatbotLearnMoreButton,
                    };
                    break;
                case 'agents':
                    content = {
                        title: 'Agents',
                        subheaderPrimaryText:
                            'A real-time view of agent availability and active chats each agent is handling.',
                    };
                    break;
                case 'sms':
                    switch (subSetting) {
                        case 'enroll':
                            content = {
                                title: 'Enroll your brand and campaign',
                                subheaderPrimaryText:
                                    'Answer some questions about your business, and details about the text messages you plan to send',
                            };
                            break;
                        case 'phone-number':
                            content = {
                                title: 'Register a phone number',
                                subheaderPrimaryText:
                                    'Select a phone number to send and receive texts',
                            };
                            break;
                        default:
                            content = {
                                title: 'SMS settings',
                                subheaderPrimaryText:
                                    'Add/register a phone number, view registration status, and update messaging preferences',
                            };
                    }
                    break;
                case 'facebook':
                    content = {
                        title: 'Facebook and Instagram setup',
                        subheaderPrimaryText: 'Log in and configure your Facebook account',
                    };
                    break;
                case 'whatsapp':
                    content = {
                        title: 'WhatsApp setup',
                        subheaderPrimaryText: 'Log in and connect your WhatsApp Business account',
                    };
                    break;
                case 'chat-onboarding':
                    content = {
                        title: 'AI Chatbot',
                        subheaderPrimaryText:
                            'Use Nutshell’s AI chatbot to answer questions and generate leads on your website',
                    };
                    break;
                case 'email':
                    content = {
                        title: subSetting ? 'Email address settings' : 'Shared email inbox',
                        subheaderPrimaryText: subSetting
                            ? 'Configure forwarding and replying for your shared email address.'
                            : 'A centralized inbox for managing shared email conversations with your team.',
                        breadcrumbs: subSetting
                            ? [{to: '/inbox/settings/email', text: 'Shared email inbox'}]
                            : undefined,
                        buttons: subSetting ? (
                            <Link
                                // @ts-expect-error
                                as={RouterLink}
                                to='/inbox/email/open'
                                size='big'
                                variant='button-secondary'
                            >
                                Go to inbox
                            </Link>
                        ) : undefined,
                        glossaryButton: sharedEmailInboxLearnMoreButton,
                    };
                    break;
                default:
                    break;
            }
    }

    // @ts-expect-error
    return <PageTitleBar {...content} />;
}

export const getPageTitle = (selectedSection?: string | null): string => {
    switch (selectedSection) {
        case 'dashboard':
            return 'Dashboard';
        case 'sms':
            return 'SMS';
        case 'chat':
            return 'Chat';
        case 'settings':
            return 'Settings';
        case 'messenger':
            return 'Messenger';
        case 'instagram':
            return 'Instagram';
        case 'whatsapp':
            return 'WhatsApp';
        case 'all':
            return 'All channels';
        case 'email':
            return 'Email';
        case 'chatbot-threads':
            return 'Chatbot threads';
        default:
            return 'SMS';
    }
};

const ownerFilter = (user: User) => {
    return Routing.param({
        filter: {
            owner: {
                anyAll: 'any',
                data: [{data: user.id}],
            },
        },
    });
};

const periodFilter = () => {
    return Routing.param({filter: {period: '-d30'}});
};

export const getMessageLinks = (
    smsCounts: SmsThreadCounts | null | undefined,
    userCanAccessSettings: boolean,
    user?: User | null
): LinkConfig[] => {
    const links = [
        {
            id: 'sms-unassigned',
            linkText: 'Unassigned',
            to: `/inbox/sms/unassigned?${periodFilter()}`,
            count: smsCounts && smsCounts.unassignedCount,
            activeMatch: /^\/inbox\/sms\/unassigned(?:\/.*)?$/,
            searchMatch: /filter%5Bperiod%5D=/,
        },
    ];

    if (userCanAccessSettings) {
        links.push({
            id: 'sms-all',
            linkText: 'All',
            to: `/inbox/sms/open?${periodFilter()}`,
            count: smsCounts && smsCounts.totalCount,
            activeMatch: /^\/inbox\/sms\/(?!unassigned)[\w-]+(?:\/.*)?$/,
            searchMatch: /filter%5Bperiod%5D=/,
        });
    }

    if (user) {
        const userInboxCount =
            smsCounts &&
            smsCounts.userInboxCounts.find(
                (userInbox) => userInbox.user && userInbox.user.id === user.id
            );
        links.push({
            id: 'sms-open',
            linkText: 'Your inbox',
            to: `/inbox/sms/open?${ownerFilter(user)}`,
            count: userInboxCount ? userInboxCount.count : 0,
            activeMatch: /^\/inbox\/sms\/(?!unassigned)[\w-]+(?:\/.*)?$/,
            searchMatch: new RegExp(
                `filter%5Bowner%5D%5BanyAll%5D=any&filter%5Bowner%5D%5Bdata%5D%5B0%5D%5Bdata%5D=${user.id}(?:$|&)`
            ),
        });
    }

    return links;
};

export const getSettingsLinks = (
    userCanAccessSettings: boolean,
    activeAgentsCount: number
): LinkConfig[] => {
    const links = [];
    if (userCanAccessSettings) {
        links.push({id: 'chat-settings', linkText: 'Chat', to: '/inbox/settings/chat'});
        links.push({
            id: 'agents-settings',
            linkText: '',
            to: '/inbox/settings/agents',
            count: activeAgentsCount,
            icon: () => getAgentMenuItem(activeAgentsCount),
        });

        links.push({id: 'sms-settings', linkText: 'SMS', to: '/inbox/settings/sms'});
    }

    return links;
};

function getAgentMenuItem(activeAgentsCount: number) {
    return (
        <Stack spacing={8} alignItems='center' isHorizontal={true}>
            <Body>Agents</Body>
            <StatusDot color={activeAgentsCount > 0 ? colors.success : colors.greyLt} size={8} />
        </Stack>
    );
}

export const getChatLinks = (
    chatCounts: ChatThreadCounts | null | undefined,
    userCanAccessSettings: boolean,
    user?: User | null
): LinkConfig[] => {
    const links = [
        {
            id: 'chat-unassigned',
            linkText: 'Unassigned',
            to: `/inbox/chat/unassigned?${periodFilter()}`,
            count: chatCounts && chatCounts.unassignedCount,
            activeMatch: /^\/inbox\/chat\/unassigned(?:\/.*)?$/,
            searchMatch: /filter%5Bperiod%5D=/,
        },
    ];

    if (userCanAccessSettings) {
        links.push({
            id: 'chat-all',
            linkText: 'All',
            to: `/inbox/chat/open?${periodFilter()}`,
            count: chatCounts && chatCounts.totalCount,
            activeMatch: /^\/inbox\/chat\/(?!unassigned)[\w-]+(?:\/.*)?$/,
            searchMatch: /filter%5Bperiod%5D=/,
        });
    }

    if (user) {
        const userInboxCount =
            chatCounts &&
            chatCounts.userInboxCounts.find(
                (userInbox) => userInbox.user && userInbox.user.id === user.id
            );
        links.push({
            id: 'chat-open',
            linkText: 'Your inbox',
            to: `/inbox/chat/open?${ownerFilter(user)}`,
            count: userInboxCount ? userInboxCount.count : 0,
            activeMatch: /^\/inbox\/chat\/(?!unassigned)[\w-]+(?:\/.*)?$/,
            searchMatch: new RegExp(
                `filter%5Bowner%5D%5BanyAll%5D=any&filter%5Bowner%5D%5Bdata%5D%5B0%5D%5Bdata%5D=${user.id}(?:$|&)`
            ),
        });
    }

    return links;
};

export function notifyUser(htmlUrlPath: string) {
    addNotification('Success!', {
        body: 'Saved successfully',
        icon: 'check',
        onClick: () => {
            appHistory.push(htmlUrlPath);
        },
    });
}

export function shouldHidePageTitleBar(
    selectedSection?: string | null,
    selectedPage?: string | null
): boolean {
    return (
        selectedSection === 'settings' &&
        (selectedPage === 'chat-onboarding' || selectedPage === 'test-chatbot')
    );
}

export function shouldHideSidebar(pathname: string) {
    return (
        pathname.includes('/inbox/settings/chat-onboarding') ||
        pathname.includes('/inbox/settings/test-chatbot')
    );
}
